import React from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

// Google's own rendered button lives in a cross-origin iframe and only ships the
// locales Google supports, which does not include Azerbaijani. We therefore use
// the authorization-code flow, which lets us render a real, translated button of
// our own: the popup hands us a one-time code and the API exchanges it for the
// ID token server-side.

const GoogleIcon: React.FC = () => (
  <svg viewBox="0 0 48 48" className="w-[18px] h-[18px] flex-shrink-0" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24s.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

interface GoogleAuthButtonProps {
  mode: "signin" | "signup";
  /** Receives the one-time authorization code to send to `auth/google`. */
  onSuccess: (authCode: string) => void;
  onError: () => void;
  loading?: boolean;
}

const GoogleAuthButton: React.FC<GoogleAuthButtonProps> = ({ mode, onSuccess, onError, loading }) => {
  const { t } = useTranslation();

  const login = useGoogleLogin({
    flow: "auth-code",
    onSuccess: response => onSuccess(response.code),
    onError: () => onError(),
    // Dismissing the popup isn't a failure, but a blocked popup is — otherwise the click looks dead.
    onNonOAuthError: error => {
      if (error.type !== "popup_closed") onError();
    },
  });

  const label = mode === "signup" ? t("auth.signUpWithGoogle") : t("auth.signInWithGoogle");

  return (
    <button
      type="button"
      onClick={() => login()}
      disabled={loading}
      className={cn(
        "mx-auto flex h-10 w-full max-w-[400px] items-center justify-center gap-2.5 rounded-md px-4 transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900",
        // dark: variants follow the resolved theme, so "system" on a dark OS gets this too.
        "bg-[#1a73e8] text-white hover:bg-[#1765cc] dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700",
        loading && "cursor-not-allowed opacity-70"
      )}
    >
      <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-white">
        <GoogleIcon />
      </span>
      <span className="truncate text-sm font-semibold">{label}</span>
    </button>
  );
};

export default GoogleAuthButton;
